import { genresOf, regionsOf, yearsOf, type MediaType } from "@/lib/media";

export interface FilterState {
  genres: string[];
  regions: string[];
  years: number[];
}

interface FilterPanelProps {
  type: MediaType;
  filters: FilterState;
  onToggle: (group: "genres" | "regions" | "years", value: string) => void;
  onClear: () => void;
}

interface GroupConfig {
  key: "genres" | "regions" | "years";
  title: string;
  english: string;
  options: string[];
}

export default function FilterPanel({ type, filters, onToggle, onClear }: FilterPanelProps) {
  const groups: GroupConfig[] = [
    { key: "genres", title: "类型", english: "Genre", options: genresOf(type) },
    { key: "regions", title: "地区", english: "Region", options: regionsOf(type) },
    { key: "years", title: "年份", english: "Year", options: yearsOf(type).map(String) },
  ];

  const totalSelected = filters.genres.length + filters.regions.length + filters.years.length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between border-b border-background-200 px-4 py-3.5">
        <div className="flex items-center gap-2">
          <i className="ri-equalizer-line text-sm text-primary-600" />
          <h2 className="text-sm font-semibold text-foreground-900">筛选条件</h2>
          {totalSelected > 0 && (
            <span className="rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700">
              {totalSelected}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="cursor-pointer text-[11px] text-foreground-400 transition-colors hover:text-primary-600"
        >
          重置
        </button>
      </div>

      <div className="divide-y divide-background-200">
        {groups.map((group) => (
          <div key={group.key} className="px-4 py-4">
            <div className="mb-3 flex items-baseline justify-between">
              <h3 className="text-xs font-medium text-foreground-800">{group.title}</h3>
              <span className="text-[10px] uppercase tracking-[0.18em] text-foreground-300">
                {group.english}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {group.options.map((option) => {
                const selected =
                  group.key === "years"
                    ? filters.years.includes(Number(option))
                    : filters[group.key].includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onToggle(group.key, option)}
                    aria-pressed={selected}
                    className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
                      selected
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-b-lg bg-secondary-50 px-4 py-3">
        <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-foreground-500">
          <i className="ri-information-line mt-0.5 shrink-0 text-secondary-600" />
          类型、地区、年份均支持多选，选择后会立即更新右侧列表。
        </p>
      </div>
    </div>
  );
}